/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Button, IModuleProps, INodeProps, isMobile, VariantType, addThrottledEvent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IBritaxSubscribeProps } from './britax-subscribe.props.autogenerated';
import { ArrowIcon } from './mocks/utils/britax-onlick-arrow';
import * as DataActionExtension from '../../actions/DataActionExtension.g';
import { IBritaxSubscribeData } from './britax-subscribe.data';
import { computed } from 'mobx';
interface errorState {
    email?: string | undefined;
    iconClassnames: string;
    message: string;
    isOnlyMobile: boolean;
}

/**
 * Subscribe view props.
 */
export interface ISubscribeViewProps extends IBritaxSubscribeProps<IBritaxSubscribeData> {
    subscribe: IModuleProps;
    subscribeContainer: INodeProps;
    inputContainer: INodeProps;
    heading: React.ReactNode;
    text: React.ReactNode;
    submitButton?: INodeProps;
    submitButtonLabelText?: string;
    subscribeForm?: INodeProps;
    emailInput?: INodeProps;
    handleText(): void;
    ArrowIcon?: React.ReactNode;
    errorMessageNode?: React.ReactNode;
    isOnlyMobile: boolean;
}
export interface ISubscribeViewPropsExtended extends IBritaxSubscribeProps<IBritaxSubscribeData> {}

/**
 * Subscribe component.
 * @extends {React.PureComponent<IBritaxSubscribeProps<IBritaxSubscribeData>>}
 */
export class Subscribe extends React.PureComponent<IBritaxSubscribeProps<IBritaxSubscribeData>, errorState> {
    private readonly _emailAddressInputRef: React.RefObject<HTMLInputElement>;
    private customResizeThrottledEventHandler?: (event: Event) => void;
    @computed public get isOnlyMobile(): boolean {
        return isMobile({ variant: VariantType.Browser, context: this.props.context.request }) === 'xs';
    }
    public componentDidMount(): void {
        this.customResizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._customUpdateViewport as EventListener);
        this._customUpdateViewport();
        window.addEventListener('hashchange', this._handleHashChange);
    }
    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.customResizeThrottledEventHandler!, false);
        window.removeEventListener('hashchange', this._handleHashChange);
    }
    public constructor(props: ISubscribeViewPropsExtended) {
        super(props);
        this._emailAddressInputRef = React.createRef();
        this._handleHashChange = this._handleHashChange.bind(this);
        this.state = {
            iconClassnames: 'default',
            email: '',
            message: '',
            isOnlyMobile: false
        };
    }

    public render(): JSX.Element | null {
        const { heading, paragraph } = this.props.config;
        const {
            subscribeValidErrorMessage,
            subscribeEmptyErrorMessage,
            subscribeGenericErrorMessage,
            subscribeSuccessMessage
        } = this.props.resources;
        const { message, iconClassnames, isOnlyMobile } = this.state;
        const moduleClassName = 'ms-subscribe';

        const subscribeParagraph = paragraph && (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className='ms-subscribe__details__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
        const onClickHandler = (): void => {
            const email = this._emailAddressInputRef.current?.value;
            const cutsAccount = (this.props.data.accountInformation && this.props.data.accountInformation.result?.AccountNumber) ?? '';
            if (!email) {
                this.setState({ message: subscribeEmptyErrorMessage, iconClassnames: 'error' });
            } else if (validateEmail(email)) {
                const sendDataPromise = new Promise((resolve, reject) => {
                    DataActionExtension.sendSubscriptionEmailAsync({ callerContext: this.props.context.actionContext }, email, cutsAccount)
                        .then(response => {
                            resolve(response);
                        })
                        .catch(error => {
                            reject(error);
                        });
                });
                sendDataPromise
                    .then(response => {
                        this.setState({ message: subscribeSuccessMessage, iconClassnames: 'success' });
                    })
                    .catch(error => {
                        this.setState({ message: subscribeGenericErrorMessage, iconClassnames: 'error' });
                    });
            } else {
                this.setState({ message: subscribeValidErrorMessage, iconClassnames: 'error' });
            }
        };

        const validateEmail = (email: string) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        };

        const subscribeHeading = heading && (
            <Msdyn365.Text
                className='ms-subscribe__details__heading'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );
        const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
            this.setState({ email: e.target.value });
        };
        //console.log(isOnlyMobile);
        const viewProps = {
            ...this.props,
            handleText: this.handleTextChange,
            subscribe: {
                moduleProps: this.props,
                className: moduleClassName
            },
            subscribeContainer: {
                className: 'ms-subscribe__details'
            },
            inputContainer: {
                className: 'ms-subscribe__input__container'
            },
            heading: subscribeHeading,
            text: subscribeParagraph,
            subscribeForm: {
                className: 'ms-subscribe__details__form',
                tag: 'form',
                'aria-label': this.props.resources.subscribeFormAriaLabel,
                name: 'subscribeForm',
                role: 'form',
                autoComplete: 'off'
            },
            emailInput: {
                className: 'ms-subscribe__details__form__email',
                tag: 'input',
                ref: this._emailAddressInputRef,
                type: 'email',
                'aria-label': this.props.resources.subscribeFormEmailAriaLabel,
                placeholder: this.props.resources.subscribeEmailPlaceholderText,
                onchange: { onChangeHandler }
            },
            submitButton: {
                className: 'ms-subscribe__details__form__submit',
                tag: Button,
                'aria-label': this.props.resources.subscribeFormSubmitAriaLabel,
                type: 'submit',
                title: this.props.resources.subscribeButtonLabel
            },
            isOnlyMobile: isOnlyMobile,
            submitButtonLabelText: this.props.resources.subscribeButtonLabel,
            ArrowIcon: <ArrowIcon onClickArrow={onClickHandler} iconClassnames={iconClassnames} />,
            errorMessageNode: <div className='ms-subscribe__details__form__message'>{message}</div>
        };

        return this.props.renderView(viewProps);
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };
    /**
     * Custom update viewport.
     */
    private readonly _customUpdateViewport = (): void => {
        this.setState({ isOnlyMobile: this.isOnlyMobile });
    };

    /**
     * Handle hash change.
     */
    private _handleHashChange(): void {
        if (window.location.hash === '#subscribe') {
            this._emailAddressInputRef.current?.focus();
            // It just for trigger event on mobile.
            this._emailAddressInputRef.current?.setAttribute('class', 'nothing');
            this._emailAddressInputRef.current?.setAttribute('id', 'subscribe');
        }
    }
}

export default Subscribe;
